import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PortalPage, Spinner } from "@/components/PortalPage";
import { api } from "@/lib/api";

const UNITS = ["each", "sq_ft", "lin_ft", "hours", "days", "other"];
const blank = { name: "", category: "", scope: "", reqs: "", comps: "", opts: "", exclusions: "", terms: "", change_note: "" };
const box = "w-full rounded-lg border border-[#444] bg-[#121212] px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--accent)]";
const lines = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean);
const unit = (u) => (UNITS.includes((u || "").trim()) ? u.trim() : "each");

const toBody = (f) => ({
  name: f.name, category: f.category, scope: f.scope, exclusions: f.exclusions, terms: f.terms, change_note: f.change_note,
  measurement_requirements: lines(f.reqs),
  components: lines(f.comps).map((l) => { const [name, u, method, inc] = l.split("|").map((x) => (x || "").trim()); return { name, unit: unit(u), method: method || "", included_in_base: /^y/i.test(inc || "") }; }),
  optional_services: lines(f.opts).map((l) => { const [description, u] = l.split("|").map((x) => (x || "").trim()); return { description, unit: unit(u) }; }),
});
const toForm = (t) => ({
  name: t.name, category: t.category, scope: t.scope, exclusions: t.exclusions, terms: t.terms, change_note: "",
  reqs: (t.measurement_requirements || []).join("\n"),
  comps: (t.components || []).map((c) => [c.name, c.unit, c.method, c.included_in_base ? "yes" : "no"].join(" | ")).join("\n"),
  opts: (t.optional_services || []).map((o) => [o.description, o.unit].join(" | ")).join("\n"),
});

const Field = ({ label, k, f, setF, rows = 1, hint }) => (
  <label className="block text-xs text-neutral-400">{label}{hint && <span className="ml-1 text-neutral-500">({hint})</span>}
    {rows > 1 ? <textarea rows={rows} className={box} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tpl-${k}`} />
      : <input className={box} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tpl-${k}`} />}
  </label>
);

export default function QuoteTemplates() {
  const [all, setAll] = useState(null);
  const [f, setF] = useState(blank);
  const [key, setKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api("/admin/quote-templates").then(setAll).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try { const t = await api(`/admin/quote-templates${key ? `?key=${key}` : ""}`, { method: "POST", body: toBody(f) }); toast.success(`Published "${t.name}" v${t.version}`); setF(blank); setKey(null); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const retire = async (k) => { if (!window.confirm("Retire this template? Quotes already made from it keep their copy.")) return; await api(`/admin/quote-templates/${k}/retire`, { method: "POST" }); load(); };
  return (
    <PortalPage eyebrow="CORPORATE ADMIN" title="Quote Templates" testId="quote-templates-page" width="max-w-5xl">
      <p className="mb-4 text-sm text-neutral-400">ALR-approved wording for quotes. Each change publishes a new version, and quotes already sent or accepted keep the version they used. Templates hold no prices: franchisees price each line with the Quote Builder or their own rates.</p>
      <div className="grid gap-3 rounded-2xl border border-[#333] bg-[#141414] p-4" data-testid="tpl-form">
        <p className="text-xs font-extrabold tracking-[1.5px] text-[var(--accent)]">{key ? "PUBLISH A NEW VERSION" : "NEW TEMPLATE"}</p>
        <div className="grid gap-3 md:grid-cols-2"><Field label="Name" k="name" f={f} setF={setF} /><Field label="Category" k="category" f={f} setF={setF} hint="e.g. Kitchen cabinets" /></div>
        <Field label="Scope (customer-facing service description)" k="scope" f={f} setF={setF} rows={4} />
        <Field label="Measurements needed" k="reqs" f={f} setF={setF} rows={3} hint="one per line" />
        <Field label="Internal components (never shown to customers)" k="comps" f={f} setF={setF} rows={3} hint="name | unit | calculation method | included in base yes/no" />
        <Field label="Optional services" k="opts" f={f} setF={setF} rows={3} hint="description | unit" />
        <Field label="Exclusions" k="exclusions" f={f} setF={setF} rows={2} />
        <Field label="Terms" k="terms" f={f} setF={setF} rows={4} />
        <Field label="What changed (for the version history)" k="change_note" f={f} setF={setF} />
        <div className="flex gap-2">
          <button disabled={busy || !f.name.trim()} onClick={save} className="rounded-full bg-[var(--accent)] px-5 py-2 text-xs font-extrabold text-black disabled:opacity-40" data-testid="tpl-publish-btn">{busy ? "Publishing…" : "Publish"}</button>
          {key && <button onClick={() => { setKey(null); setF(blank); }} className="rounded-full border border-[#444] px-4 py-2 text-xs font-bold" data-testid="tpl-cancel-btn">Cancel</button>}
        </div>
      </div>
      {!all ? <Spinner /> : (
        <div className="mt-4 rounded-2xl border border-[#333] bg-[#141414] px-4" data-testid="tpl-list">
          {!all.length && <p className="py-6 text-center text-xs text-neutral-400">No templates yet.</p>}
          {all.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-2 border-t border-[#262626] py-3 text-sm first:border-t-0" data-testid={`tpl-row-${t.id}`}>
              <b>{t.name}</b><span className="text-xs text-neutral-400">v{t.version} · {t.status} · {t.created_by} · {new Date(t.created_at).toLocaleDateString()}</span>
              {t.status === "published" && <>
                <button onClick={() => { setKey(t.key); setF(toForm(t)); window.scrollTo(0, 0); }} className="ml-auto text-xs font-bold text-[var(--accent)]" data-testid={`tpl-edit-${t.id}`}>New version</button>
                <button onClick={() => retire(t.key)} className="text-xs font-bold text-[#ff8080]" data-testid={`tpl-retire-${t.id}`}>Retire</button>
              </>}
            </div>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
